/**
 * Typed server-side client for the Sola Bible App API.
 *
 * IMPORTANT: every call here must run server-side (Server Component, Route
 * Handler, or build/generation script). CORS on solabibleapp.vercel.app is
 * closed to the marketing domain (confirmed in docs-research/api-surface-report.md
 * §5) — client-side `fetch` calls from the browser will be blocked. This
 * module is never imported from a 'use client' file.
 */

export const API_BASE_URL = process.env.SOLA_API_URL || 'https://solabibleapp.vercel.app';

/**
 * Shared secret that identifies this site to the API as a first-party caller,
 * so its SSR traffic is rate limited on its own generous budget instead of
 * sharing the anonymous per-IP bucket.
 *
 * This matters more than it looks. /bible-verse-explained is force-dynamic, so
 * every page view fans out into 3-5 calls here, and all of them egress from
 * Vercel's shared IPs — meaning without this header the ENTIRE site shares one
 * anonymous bucket. Measured 2026-09-04: 57.7 req/min against a 60/min limit,
 * i.e. ~4% from the site rate-limiting itself. See the API's
 * backend/src/middleware/rateLimit.ts for the matching tiers.
 *
 * Unset is a soft failure by design: calls still succeed, just on the public
 * budget. The warning below is the signal that the env var didn't make it into
 * this environment (env vars do NOT cross Vercel projects).
 */
const SERVICE_TOKEN = process.env.SOLA_SERVICE_TOKEN;

let missingTokenWarned = false;

/** Merge the first-party service token into a request's headers. */
function serviceHeaders(extra?: HeadersInit): Record<string, string> {
  const base = (extra as Record<string, string> | undefined) ?? {};
  if (!SERVICE_TOKEN) {
    if (!missingTokenWarned) {
      console.warn(
        '[solaApi] SOLA_SERVICE_TOKEN is not set — SSR calls fall back to the ' +
        'anonymous per-IP rate limit, shared across all traffic from this site.'
      );
      missingTokenWarned = true;
    }
    return base;
  }
  return { ...base, 'x-sola-service-token': SERVICE_TOKEN };
}

// Revalidate windows. Verse text and insights content are effectively
// immutable (insights are cached server-side for 30 days per the API
// report), so we cache long and let ISR quietly refresh in the background.
const REVALIDATE_VERSE_TEXT = 60 * 60 * 24 * 7; // 7 days
const REVALIDATE_INSIGHTS = 60 * 60 * 24 * 14; // 14 days
const REVALIDATE_STRUCTURE = 60 * 60 * 24 * 30; // 30 days
const REVALIDATE_SEARCH = 0; // never cache autocomplete

export interface FetchedVerse {
  verse: number;
  text: string;
}

export interface FetchedChapter {
  reference: string;
  verses: FetchedVerse[];
  translation?: string;
}

export interface CommentarySection {
  title: string;
  content: string;
}

export interface CrossReferenceItem {
  reference: string;
  connection: string;
}

export interface VerseInsightsBundle {
  narrativeContext?: string;
  keyInsight?: string;
  commentary?: { sections: CommentarySection[] };
  metadata?: {
    author?: string;
    location?: string;
    dateTime?: string;
    literaryStyle?: string;
  };
  themes?: { themes: string[] };
  historicalBackground?: { hasHistoricalBackground: boolean; [key: string]: unknown };
}

export interface LexiconTerm {
  term: string;
  verse: number;
  definition: string;
  originalWord: string;
  partOfSpeech: string;
  transliteration: string;
  originalLanguage: string;
}

export interface ChapterLexicon {
  reference: string;
  keyTerms: LexiconTerm[];
}

export interface ScholarlyCommentary {
  id: string;
  commentary: string;
  book: string;
  chapter: number;
  content: string;
}

/** Encode a canonical book name for use as a URL path segment. */
function encodeBook(bookName: string): string {
  return encodeURIComponent(bookName);
}

async function safeJsonFetch<T>(
  url: string,
  init: RequestInit,
  label: string
): Promise<T | null> {
  try {
    const res = await fetch(url, { ...init, headers: serviceHeaders(init.headers) });
    if (!res.ok) {
      console.error(`[solaApi] ${label} failed: ${res.status} ${res.statusText} (${url})`);
      return null;
    }
    const json = await res.json();
    return (json.data ?? json) as T;
  } catch (err) {
    console.error(`[solaApi] ${label} threw:`, err);
    return null;
  }
}

/** GET /api/chapter/{book}/{chapter}/verses — full chapter text. Throws on failure (verse text is load-bearing, not optional). */
export async function fetchChapterVerses(
  bookName: string,
  chapter: number,
  translation = 'esv'
): Promise<FetchedChapter> {
  const url = `${API_BASE_URL}/api/chapter/${encodeBook(bookName)}/${chapter}/verses?translation=${translation}`;
  const res = await fetch(url, {
    headers: serviceHeaders(),
    next: { revalidate: REVALIDATE_VERSE_TEXT },
  });
  if (!res.ok) {
    throw new Error(`fetchChapterVerses failed: ${res.status} ${res.statusText} (${url})`);
  }
  const json = await res.json();
  return json.data as FetchedChapter;
}

/** GET /api/chapter/structure — used only by the generation script; the app imports the committed JSON instead. */
export async function fetchChapterStructure(
  translation = 'esv'
): Promise<Record<string, Record<string, number>>> {
  const url = `${API_BASE_URL}/api/chapter/structure?translation=${translation}`;
  const res = await fetch(url, {
    headers: serviceHeaders(),
    next: { revalidate: REVALIDATE_STRUCTURE },
  });
  if (!res.ok) {
    throw new Error(`fetchChapterStructure failed: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();
  return json.data.structure;
}

/** POST /api/verse-insights/{book}/{chapter}/{verse}/bundle — narrative, key insight, commentary, metadata, themes, historical background in one call. */
export async function fetchVerseInsightsBundle(
  bookName: string,
  chapter: number,
  verse: number,
  verseText: string,
  translation = 'esv'
): Promise<VerseInsightsBundle | null> {
  const url = `${API_BASE_URL}/api/verse-insights/${encodeBook(bookName)}/${chapter}/${verse}/bundle`;
  return safeJsonFetch<VerseInsightsBundle>(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verseText, translation }),
      next: { revalidate: REVALIDATE_INSIGHTS },
    },
    'fetchVerseInsightsBundle'
  );
}

/**
 * POST /api/verse-insights/{book}/{chapter}/{verse}/key-insight
 * Fallback for when the bundle endpoint returns a null keyInsight (observed
 * live — the bundle's own docs describe "graceful null on individual
 * failure," and this single-field endpoint reliably succeeds even when the
 * bundle call returns null for it).
 */
export async function fetchKeyInsight(
  bookName: string,
  chapter: number,
  verse: number,
  verseText: string,
  translation = 'esv'
): Promise<string | null> {
  const url = `${API_BASE_URL}/api/verse-insights/${encodeBook(bookName)}/${chapter}/${verse}/key-insight`;
  const data = await safeJsonFetch<{ keyInsight: string }>(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verseText, translation }),
      next: { revalidate: REVALIDATE_INSIGHTS },
    },
    'fetchKeyInsight'
  );
  return data?.keyInsight ?? null;
}

/** POST /api/verse-insights/{book}/{chapter}/{verse}/narrative-context — same fallback rationale as fetchKeyInsight above. */
export async function fetchNarrativeContext(
  bookName: string,
  chapter: number,
  verse: number,
  verseText: string,
  translation = 'esv'
): Promise<string | null> {
  const url = `${API_BASE_URL}/api/verse-insights/${encodeBook(bookName)}/${chapter}/${verse}/narrative-context`;
  const data = await safeJsonFetch<{ narrativeContext: string }>(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verseText, translation }),
      next: { revalidate: REVALIDATE_INSIGHTS },
    },
    'fetchNarrativeContext'
  );
  return data?.narrativeContext ?? null;
}

/**
 * POST /api/verse-insights/{book}/{chapter}/{verse}/cross-references
 * Not included in the bundle response as of 2026-07-09 (verified live) despite
 * the bundle route's doc description listing it — fetched as a separate call.
 */
export async function fetchCrossReferences(
  bookName: string,
  chapter: number,
  verse: number,
  verseText: string,
  translation = 'esv'
): Promise<CrossReferenceItem[] | null> {
  const url = `${API_BASE_URL}/api/verse-insights/${encodeBook(bookName)}/${chapter}/${verse}/cross-references`;
  const data = await safeJsonFetch<{ references: CrossReferenceItem[] }>(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verseText, translation }),
      next: { revalidate: REVALIDATE_INSIGHTS },
    },
    'fetchCrossReferences'
  );
  return data?.references ?? null;
}

/** GET /api/lexicon/{book}/{chapter} — chapter-level original-language word breakdown. */
export async function fetchChapterLexicon(
  bookName: string,
  chapter: number
): Promise<ChapterLexicon | null> {
  const url = `${API_BASE_URL}/api/lexicon/${encodeBook(bookName)}/${chapter}`;
  return safeJsonFetch<ChapterLexicon>(
    url,
    { next: { revalidate: REVALIDATE_INSIGHTS } },
    'fetchChapterLexicon'
  );
}

/** GET /api/commentaries/verse/{book}/{chapter}/{verse} — curated scholarly commentary (e.g. Barnes), for citations. */
export async function fetchScholarlyCommentary(
  bookName: string,
  chapter: number,
  verse: number
): Promise<ScholarlyCommentary[] | null> {
  const url = `${API_BASE_URL}/api/commentaries/verse/${encodeBook(bookName)}/${chapter}/${verse}`;
  return safeJsonFetch<ScholarlyCommentary[]>(
    url,
    { next: { revalidate: REVALIDATE_INSIGHTS } },
    'fetchScholarlyCommentary'
  );
}

/** GET /api/search/suggestions?query= — autocomplete suggestions (natural-language questions). */
export async function fetchSearchSuggestions(query: string): Promise<string[]> {
  const url = `${API_BASE_URL}/api/search/suggestions?query=${encodeURIComponent(query)}`;
  const data = await safeJsonFetch<{ suggestions: string[] }>(
    url,
    { next: { revalidate: REVALIDATE_SEARCH } },
    'fetchSearchSuggestions'
  );
  return data?.suggestions ?? [];
}
